import { SagaIterator } from 'redux-saga'
import * as effects from 'redux-saga/effects'
import * as api from './api'
import * as actions from './actions'
import { formatErrorMessage } from '../../utils/helpers'
import * as Sentry from '@sentry/react'
import {
  DataCreateBlockType,
  UpdateBlockDataType,
  PayloadSimpleTaskType,
  PayloadUpdateSimpleTaskType,
} from './types'

function* fetchLessonBlocks(payload: { lid: string }): SagaIterator {
  try {
    const lesson = yield effects.call(api.fetchLesson, payload.lid)
    yield effects.put(actions.fetchLessonBlocksSuccess(lesson.data.blocks))
  } catch (error) {
    console.warn('Cannot fetch lesson blocks', error)
    yield effects.put(
      actions.fetchLessonBlocksFailure(formatErrorMessage(error))
    )
    Sentry.captureException(error)
  }
}

function* updateBlock(payload: {
  lid: string
  block_id: string
  data: UpdateBlockDataType
}): SagaIterator {
  try {
    const { block_id, data, lid } = payload
    yield effects.call(api.updateBlock, block_id, data)
    yield effects.put(actions.updateBlockSuccess())
    yield effects.put(actions.fetchLessonBlocks(lid))
  } catch (error) {
    console.warn('Cannot update block', error)
    yield effects.put(actions.updateBlockFailure(formatErrorMessage(error)))
    Sentry.captureException(error)
  }
}

function* sortBlocks(payload: { lid: string; ids: string[] }): SagaIterator {
  try {
    const { lid, ids } = payload
    yield effects.call(api.sortBlocks, lid, ids)
    yield effects.put(actions.sortBlocksSuccess())
    yield effects.put(actions.fetchLessonBlocks(lid))
  } catch (error) {
    console.warn('Cannot sort blocks', error)
    yield effects.put(actions.sortBlocksFailure(formatErrorMessage(error)))
    Sentry.captureException(error)
  }
}

function* deleteBlock(payload: {
  lid: string
  block_id: string
}): SagaIterator {
  try {
    const { lid, block_id } = payload
    yield effects.call(api.deleteBlock, block_id)
    yield effects.put(actions.deleteBlockSuccess())
    yield effects.put(actions.fetchLessonBlocks(lid))
  } catch (error) {
    console.warn('Cannot delete block', error)
    yield effects.put(actions.deleteBlockFailure(formatErrorMessage(error)))
    Sentry.captureException(error)
  }
}

function* createBlock(payload: {
  lid: string
  data: DataCreateBlockType
  position: number
}): SagaIterator {
  try {
    const { lid, data, position } = payload
    const block = yield effects.call(api.createBlock, data)
    yield effects.call(
      api.attachLessonBlock,
      lid,
      block.data.block_id,
      position
    )
    yield effects.put(actions.fetchLessonBlocks(lid))
    yield effects.put(actions.createBlockSuccess())
  } catch (error) {
    console.warn('Cannot create block', error)
    yield effects.put(actions.createBlockFailure(formatErrorMessage(error)))
    Sentry.captureException(error)
  }
}

function* createSimpleTask(payload: PayloadSimpleTaskType): SagaIterator {
  try {
    const {
      lid,
      position,
      module_id,
      typeSimpleTask,
      notion_description_id,
      caption,
      description,
      show_field,
      field_caption,
      field_required,
      show_file,
      file_caption,
      file_required,
      task_required,
    } = payload
    const simpleTask = yield effects.call(
      api.createSimpleTask,
      typeSimpleTask === 'text' ? null : notion_description_id,
      caption,
      description,
      show_field,
      field_caption,
      field_required,
      show_file,
      file_caption,
      file_required,
      task_required
    )
    yield effects.put(actions.createSimpleTaskSuccess())
    yield effects.put(
      actions.createBlock(
        lid,
        {
          type: 'simple-task',
          caption: 'Simple Task',
          payload: {
            simple_task_id: simpleTask.data.simple_task_id,
            module_id,
          },
        },
        position
      )
    )
  } catch (error) {
    console.warn('Cannot create simple task', error)
    yield effects.put(
      actions.createSimpleTaskFailure(formatErrorMessage(error))
    )
    Sentry.captureException(error)
  }
}

function* fetchModules(payload: { course_id: string }): SagaIterator {
  try {
    const modules = yield effects.call(api.fetchModules, payload.course_id)
    yield effects.put(actions.fetchModulesSuccess(modules.data))
  } catch (error) {
    console.warn('Cannot fetch modules', error)
    yield effects.put(actions.fetchModulesFailure(formatErrorMessage(error)))
    Sentry.captureException(error)
  }
}

function* updateSimpleTask(payload: PayloadUpdateSimpleTaskType): SagaIterator {
  try {
    const {
      lid,
      simple_task_id,
      block_id,
      module_id,
      typeSimpleTask,
      notion_description_id,
      caption,
      description,
      show_field,
      field_caption,
      field_required,
      show_file,
      file_caption,
      file_required,
      task_required,
    } = payload
    const simpleTask = yield effects.call(
      api.updateSimpleTask,
      simple_task_id,
      typeSimpleTask === 'text' ? null : notion_description_id,
      caption,
      description,
      show_field,
      field_caption,
      field_required,
      show_file,
      file_caption,
      file_required,
      task_required
    )
    yield effects.put(actions.updateSimpleTaskSuccess())
    yield effects.put(
      actions.updateBlock(lid, block_id, {
        payload: {
          simple_task_id: simpleTask.data.simple_task_id,
          module_id,
        },
      })
    )
  } catch (error) {
    console.warn('Cannot update simple task', error)
    yield effects.put(
      actions.updateSimpleTaskFailure(formatErrorMessage(error))
    )
    Sentry.captureException(error)
  }
}

function* fetchSimpleTaskNotion(payload: {
  simple_task_id: string
}): SagaIterator {
  try {
    yield effects.call(api.fetchSimpleTaskNotion, payload.simple_task_id)
    yield effects.put(actions.fetchSimpleTaskNotionSuccess())
  } catch (error) {
    console.warn('Cannot fetch simple task notion', error)
    yield effects.put(
      actions.fetchSimpleTaskNotionFailure(formatErrorMessage(error))
    )
    Sentry.captureException(error)
  }
}

function* fetchNotion(payload: {
  block_id: string
  notion_page_id: string
}): SagaIterator {
  try {
    const { block_id, notion_page_id } = payload
    yield effects.call(api.fetchNotion, block_id, notion_page_id)
    yield effects.put(actions.fetchNotionSuccess())
  } catch (error) {
    console.warn('Cannot fetch notion', error)
    yield effects.put(actions.fetchNotionFailure(formatErrorMessage(error)))
    Sentry.captureException(error)
  }
}

function* watchFetchLessonBlocks(): SagaIterator {
  yield effects.takeEvery(
    actions.fetchLessonBlocks.toString(),
    fetchLessonBlocks
  )
}

function* watchUpdateBlock(): SagaIterator {
  yield effects.takeEvery(actions.updateBlock.toString(), updateBlock)
}

function* watchSortBlocks(): SagaIterator {
  yield effects.takeEvery(actions.sortBlocks.toString(), sortBlocks)
}

function* watchDeleteBlock(): SagaIterator {
  yield effects.takeEvery(actions.deleteBlock.toString(), deleteBlock)
}

function* watchCreateBlock(): SagaIterator {
  yield effects.takeEvery(actions.createBlock.toString(), createBlock)
}

function* watchCreateSimpleTask(): SagaIterator {
  yield effects.takeEvery(actions.createSimpleTask.toString(), createSimpleTask)
}

function* watchFetchModules(): SagaIterator {
  yield effects.takeEvery(actions.fetchModules.toString(), fetchModules)
}

function* watchUpdateSimpleTask(): SagaIterator {
  yield effects.takeEvery(actions.updateSimpleTask.toString(), updateSimpleTask)
}

function* watchFetchSimpleTaskNotion(): SagaIterator {
  yield effects.takeEvery(
    actions.fetchSimpleTaskNotion.toString(),
    fetchSimpleTaskNotion
  )
}

function* watchFetchNotion(): SagaIterator {
  yield effects.takeEvery(actions.fetchNotion.toString(), fetchNotion)
}

export default function* watchAdminLessonBlocks(): SagaIterator {
  yield effects.all([
    effects.fork(watchFetchLessonBlocks),
    effects.fork(watchUpdateBlock),
    effects.fork(watchSortBlocks),
    effects.fork(watchDeleteBlock),
    effects.fork(watchCreateBlock),
    effects.fork(watchCreateSimpleTask),
    effects.fork(watchFetchModules),
    effects.fork(watchUpdateSimpleTask),
    effects.fork(watchFetchSimpleTaskNotion),
    effects.fork(watchFetchNotion),
  ])
}
